/**
 * VendorGoogleReviewsCard — Iter 67al.
 *
 * Dedicated section on the Vendor Dashboard for managing the vendor's Google
 * Business Profile Place ID. Although the same input lives inside the broader
 * "Business profile" form, vendors kept missing it — so we surface it here as
 * a stand-alone card with:
 *
 *   - A status pill: "Live" (api-key set + place-id set) /
 *                    "Awaiting platform setup" (no api key — admin's job) /
 *                    "Not connected" (api-key set, place-id missing).
 *   - A single Place ID input + Save button (PATCH /users/me/business).
 *   - A live preview that immediately fetches and renders the vendor's
 *     reviews via /api/vendors/{id}/google-reviews so they can verify the
 *     Place ID is correct without leaving the page.
 *   - Helpful microcopy + a deep link to Google's "find my Place ID" tool.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, Star, ExternalLink, CheckCircle2, AlertCircle, Globe2 } from "lucide-react";

export default function VendorGoogleReviewsCard() {
  const { user, refresh } = useAuth();
  const [placeId, setPlaceId] = useState(user?.google_place_id || "");
  const [saving, setSaving] = useState(false);
  const [platformStatus, setPlatformStatus] = useState({ enabled: null });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Sync input when the user record refreshes (e.g. after save)
  useEffect(() => { setPlaceId(user?.google_place_id || ""); }, [user?.google_place_id]);

  // Check whether the platform-level Google Places key is wired. This is the
  // admin's job — when it's off, we tell the vendor explicitly so they don't
  // think their Place ID is the problem.
  useEffect(() => {
    api.get("/meta/google-reviews-status")
      .then((r) => setPlatformStatus(r.data))
      .catch(() => setPlatformStatus({ enabled: false }));
  }, []);

  // Live preview — fetched on mount and after save so the vendor can see the
  // exact reviews that will appear on their car pages and the homepage.
  const fetchPreview = async () => {
    if (!user?.id) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/vendors/${user.id}/google-reviews`);
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };
  useEffect(() => { fetchPreview(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/business", { google_place_id: placeId.trim() });
      toast.success("Google Place ID saved");
      await refresh?.();
      // Give the backend a beat to refresh the cache, then re-pull the preview.
      setTimeout(fetchPreview, 400);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Tri-state status driven by the combination of platform-key + vendor-place-id
  let status = "loading";
  if (platformStatus.enabled === null) status = "loading";
  else if (!platformStatus.enabled) status = "platform-off";
  else if (!placeId.trim()) status = "not-connected";
  else if (preview && preview.reviews && preview.reviews.length > 0) status = "live";
  else status = "connected-but-no-data";

  return (
    <div className="rounded-3xl border border-border bg-white shadow-sm overflow-hidden" data-testid="vendor-google-reviews-card">
      {/* Header strip with the Google glyph + status pill */}
      <div className="flex items-center justify-between gap-4 px-6 py-5 bg-gradient-to-br from-blue-50/40 via-white to-amber-50/40 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-white border border-border flex items-center justify-center shadow-sm">
            <GoogleGlyph className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold tracking-tight" data-testid="vendor-google-reviews-card-title">Google Reviews</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Pull live reviews from your Google Business Profile.</p>
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: input + helper copy */}
        <div className="space-y-4">
          <div>
            <Label className="font-medium">Your Google Place ID</Label>
            <Input
              className="mt-1.5 rounded-xl font-mono text-sm"
              placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
              value={placeId}
              onChange={(e) => setPlaceId(e.target.value)}
              data-testid="vendor-google-place-id-input"
            />
            <p className="text-[11px] text-neutral-500 mt-1.5 leading-relaxed">
              Open Google Maps, search for your business, and copy the Place ID using the official lookup tool.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={save}
              disabled={saving || placeId === (user?.google_place_id || "")}
              className="rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              data-testid="vendor-google-place-id-save"
            >
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save Place ID"}
            </Button>
            <a
              href="https://developers.google.com/maps/documentation/places/web-service/place-id"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium border border-border hover:border-neutral-300 hover:bg-neutral-50 transition"
              data-testid="vendor-google-place-id-finder-link"
            >
              <Globe2 className="h-3 w-3" /> Find my Place ID
              <ExternalLink className="h-3 w-3 opacity-60" />
            </a>
          </div>

          {status === "platform-off" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex items-start gap-2.5" data-testid="vendor-google-status-platform-off">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <strong>Platform integration not yet active.</strong> An administrator hasn't connected the Google Places API key yet. You can save your Place ID now — reviews will appear automatically as soon as the integration goes live.
              </div>
            </div>
          )}
        </div>

        {/* Right: live preview */}
        <div className="rounded-2xl border border-border bg-neutral-50/50 p-4" data-testid="vendor-google-preview">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-neutral-500 mb-3">Live preview</div>
          {previewLoading ? (
            <div className="space-y-2 animate-pulse">
              <div className="h-3 rounded-full bg-neutral-200 w-2/3" />
              <div className="h-3 rounded-full bg-neutral-200 w-1/3" />
              <div className="h-20 rounded-xl bg-neutral-200" />
            </div>
          ) : !placeId.trim() ? (
            <div className="text-xs text-neutral-500 py-8 text-center">
              Save a Place ID to see your reviews here.
            </div>
          ) : !preview || !preview.reviews || preview.reviews.length === 0 ? (
            <div className="text-xs text-neutral-500 py-8 text-center" data-testid="vendor-google-preview-empty">
              {status === "platform-off"
                ? "Will appear once the platform integration is live."
                : "No reviews returned — double-check the Place ID, or wait a moment for the cache to refresh."}
            </div>
          ) : (
            <div className="space-y-3" data-testid="vendor-google-preview-content">
              <div className="flex items-center gap-2 text-xs">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-3.5 w-3.5 ${i < Math.round(preview.rating || 0) ? "fill-amber-500 text-amber-500" : "text-neutral-300"}`} />
                  ))}
                </div>
                <span className="font-bold text-neutral-900">{Number(preview.rating || 0).toFixed(1)}</span>
                <span className="text-neutral-500">· {Number(preview.total || 0).toLocaleString()} on Google</span>
              </div>
              {preview.reviews.slice(0, 2).map((r, i) => (
                <div key={i} className="rounded-xl bg-white border border-border p-3" data-testid={`vendor-google-preview-review-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-medium text-neutral-800 truncate">{r.author}</div>
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className={`h-2.5 w-2.5 ${j < Math.round(r.rating || 0) ? "fill-amber-500 text-amber-500" : "text-neutral-300"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-neutral-600 line-clamp-3">{r.text}</p>
                </div>
              ))}
              {preview.reviews.length > 2 && (
                <div className="text-[11px] text-neutral-500 text-center">
                  + {preview.reviews.length - 2} more — visible on your car pages.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    loading:    { label: "Checking…", color: "bg-neutral-100 text-neutral-600 ring-neutral-200", icon: AlertCircle },
    "platform-off": { label: "Awaiting platform setup", color: "bg-amber-50 text-amber-700 ring-amber-200", icon: AlertCircle },
    "not-connected": { label: "Not connected", color: "bg-neutral-100 text-neutral-600 ring-neutral-200", icon: AlertCircle },
    "connected-but-no-data": { label: "Connected · syncing", color: "bg-blue-50 text-blue-700 ring-blue-200", icon: CheckCircle2 },
    live: { label: "Live", color: "bg-emerald-50 text-emerald-700 ring-emerald-200", icon: CheckCircle2 },
  };
  const m = map[status];
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] ring-1 ${m.color}`} data-testid={`vendor-google-status-${status}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

function GoogleGlyph(props) {
  return (
    <svg viewBox="0 0 48 48" {...props}>
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  );
}
